import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    const { priceContainer, volumePricingNote } = this.refs;

    if (!event.detail?.data?.html) return;

    const blockId = this.dataset.blockId;
    if (!blockId) return;

    // Always look up the matching product-price for this block id in the updated HTML,
    // regardless of which product id fired the event. This makes it robust when
    // switching between subscribe and one-time products.
    const newProductPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${blockId}"]`);

    // If the updated HTML does not contain this product-price block (for example, when
    // switching products from a featured section that isn't part of the fetched template),
    // fall back to updating the numbers directly from the variant data.
    if (!newProductPrice) {
      const variant = /** @type {any} */ (event.detail.resource);
      if (!variant || !priceContainer) return;

      const priceElement = priceContainer.querySelector('.price');
      const compareElement = priceContainer.querySelector('.compare-at-price');

      const moneyFormat =
        /** @type {any} */ (window).Shopify?.money_format || /** @type {any} */ (window).Shopify?.currency?.money_format;
      const formatMoneyFn = /** @type {any} */ (window).Shopify?.formatMoney;

      if (typeof formatMoneyFn !== 'function' || !moneyFormat) return;

      const price = variant.price;
      const compareAt = variant.compare_at_price;

      if (priceElement) {
        priceElement.textContent = formatMoneyFn(price, moneyFormat);
      }

      if (compareElement) {
        if (compareAt && compareAt > price) {
          compareElement.textContent = formatMoneyFn(compareAt, moneyFormat);
        } else {
          compareElement.textContent = '';
        }
      }

      return;
    }

    // Update tracked product id if the server-side product changed
    const newProductId = newProductPrice.dataset.productId;
    if (newProductId) {
      this.dataset.productId = newProductId;
    }

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
